import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Light is the default. Only a theme the user picked with the toggle is remembered.
 * The previous provider followed the OS preference and saved the result under "theme" on every
 * load, so that key cannot tell a real choice from the old automatic default: it is ignored and
 * removed, and explicit choices now live under CHOICE_KEY.
 */
const CHOICE_KEY = "fs:theme";
const LEGACY_KEY = "theme";

function readChoice(): Theme {
  try {
    localStorage.removeItem(LEGACY_KEY);
    const stored = localStorage.getItem(CHOICE_KEY);
    return stored === "dark" || stored === "light" ? stored : "light";
  } catch {
    return "light"; // storage blocked (private mode, embedded): default theme
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => (typeof window === "undefined" ? "light" : readChoice()));

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
  }, [theme]);

  const setTheme = (next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(CHOICE_KEY, next);
    } catch {
      /* not persisted; the choice still applies to this session */
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
